import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SidebarService } from '../../services/sidebar.service';
import { MenuService } from '../../services/menu.service';
import { Observable, of } from 'rxjs';
import { Menu } from '../../models/menu.model';
import { AuthService } from '../../services/auth.service';
import { UserComplete } from '../../models/auth.models';
import { switchMap, map, tap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  isVisible$: Observable<boolean>;
  menuItems$: Observable<Menu[]>;
  userProfile$: Observable<UserComplete | null>;
  showProfileMenu = false;
  private currentMenuItems: Menu[] = [];

  constructor(
    private sidebarService: SidebarService,
    private menuService: MenuService,
    private authService: AuthService,
    private router: Router
  ) {
    this.isVisible$ = this.sidebarService.sidebarVisible$;
    this.menuItems$ = this.authService.isLoggedIn$.pipe(
      switchMap(isLoggedIn => {
        if (isLoggedIn) {
          return this.menuService.getMenus();
        } else {
          return of([]);
        }
      }),
      map(items => this.buildMenuTree(items)),
      tap(items => this.currentMenuItems = items)
    );

    // Obtener el perfil del usuario
    this.userProfile$ = this.authService.isLoggedIn$.pipe(
      switchMap(isLoggedIn => {
        if (isLoggedIn) {
          const userId = this.authService.getCurrentUser()?.id;
          return this.authService.getAccount(userId!).pipe(
            catchError(() => of(null))
          );
        } else {
          return of(null);
        }
      })
    );
  }

  ngOnInit(): void { }

  private buildMenuTree(items: Menu[]): Menu[] {
    // Sort all items by the 'order' property first
    items.sort((a, b) => a.order - b.order);

    const tree: Menu[] = [];
    const lookup: { [key: number]: Menu } = {};

    items.forEach(item => {
      lookup[item.id] = { ...item, children: [] };
    });

    items.forEach(item => {
      if (item.parentId && lookup[item.parentId]) {
        lookup[item.parentId].children!.push(lookup[item.id]);
      } else {
        tree.push(lookup[item.id]);
      }
    });

    return tree;
  }

  toggleSubmenu(item: Menu): void {
    const wasExpanded = item.expanded;

    // Close all other submenus
    this.currentMenuItems.forEach(i => {
      if (i.expanded) {
        i.expanded = false;
      }
    });

    // Toggle the clicked item
    item.expanded = !wasExpanded;
  }

  onMenuClick(action: string | null | undefined) {
    if (action && action.startsWith('Users/')) {
      const id = action.split('/')[1];
      this.router.navigate(['/dashboard', 'subcontractors', id]);
      this.closeSidebar();
    } else if (action) {
      this.router.navigate(['/dashboard', action.toLowerCase()]);
      this.closeSidebar();
    }
  }

  closeSidebar() {
    this.sidebarService.close();
  }

  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
  }

  goToProfile() {
    const userId = this.authService.getCurrentUser()?.id;
    this.router.navigate(['/dashboard', 'profile', userId]);
    this.closeSidebar();
    this.showProfileMenu = false;
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/']);
    this.closeSidebar();
    this.showProfileMenu = false;
  }
} 